import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, Camera, Trash2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { todayISO, fmtDay, uploadHaccpPhoto, photoSrc } from "@/utils/haccp";

const SEV = { bassa: "text-zinc-300", media: "text-amber-400", alta: "text-red-400" };

export default function HaccpNonConformities() {
  const { authAxios } = useAuth();
  const fileRef = useRef(null);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", action: "", date: todayISO(), severity: "media", photo_url: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await authAxios.get("/haccp/non-conformities");
      setItems(r.data.items || []);
    } catch { toast.error("Errore caricamento non conformità"); }
  }, [authAxios]);

  useEffect(() => { load(); }, [load]);

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const t = toast.loading("Caricamento foto...");
    try {
      const url = await uploadHaccpPhoto(authAxios, file);
      setForm((f) => ({ ...f, photo_url: url }));
      toast.success("Foto allegata", { id: t });
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Errore caricamento foto", { id: t });
    } finally {
      setUploading(false);
      if (e.target) e.target.value = "";
    }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Inserisci il titolo della non conformità"); return; }
    setSaving(true);
    try {
      await authAxios.post("/haccp/non-conformities", form);
      toast.success("Non conformità registrata");
      setForm({ title: "", description: "", action: "", date: todayISO(), severity: "media", photo_url: "" });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const close = async (id) => {
    try {
      await authAxios.patch(`/haccp/non-conformities/${id}`, { status: "chiusa" });
      toast.success("Non conformità chiusa");
      await load();
    } catch { toast.error("Errore aggiornamento"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa non conformità?")) return;
    try { await authAxios.delete(`/haccp/non-conformities/${id}`); await load(); }
    catch { toast.error("Errore eliminazione"); }
  };

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="haccp-nc-page">
      <main className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto pb-16">
        <PageHeader title="Non conformità" />

        <Card className="bg-[#18181b] border-red-500/20 mb-5">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <h3 className="text-white text-base font-semibold">Nuova non conformità</h3>
            </div>
            <Input
              data-testid="nc-title-input"
              value={form.title}
              placeholder="Titolo (es. Temperatura cella fuori range)"
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="h-10 bg-[#0f0f11] border-white/10 text-white"
            />
            <div className="grid grid-cols-2 gap-2">
              <Input
                type="date"
                data-testid="nc-date-input"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="h-10 bg-[#0f0f11] border-white/10 text-white"
              />
              <select
                data-testid="nc-severity-select"
                value={form.severity}
                onChange={(e) => setForm({ ...form, severity: e.target.value })}
                className="h-10 rounded-md bg-[#0f0f11] border border-white/10 text-white text-sm px-2"
              >
                <option value="bassa">Gravità bassa</option>
                <option value="media">Gravità media</option>
                <option value="alta">Gravità alta</option>
              </select>
            </div>
            <Textarea
              data-testid="nc-description-input"
              value={form.description}
              placeholder="Descrizione del problema"
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="bg-[#0f0f11] border-white/10 text-white min-h-[70px]"
            />
            <Textarea
              data-testid="nc-action-input"
              value={form.action}
              placeholder="Azione correttiva adottata"
              onChange={(e) => setForm({ ...form, action: e.target.value })}
              className="bg-[#0f0f11] border-white/10 text-white min-h-[70px]"
            />
            <div className="flex items-center gap-3">
              <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={pickPhoto} className="hidden" data-testid="nc-photo-input" />
              <Button
                variant="outline"
                data-testid="nc-photo-btn"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
                className="h-10 border-white/10 text-zinc-300 hover:text-amber-400"
              >
                <Camera className="w-4 h-4 mr-1" /> {form.photo_url ? "Cambia foto" : "Foto (opzionale)"}
              </Button>
              {form.photo_url && (
                <img src={photoSrc(form.photo_url)} alt="allegato" className="w-12 h-12 rounded object-cover border border-white/10" data-testid="nc-photo-preview" />
              )}
            </div>
            <Button onClick={save} disabled={saving} data-testid="nc-submit-btn" className="w-full h-10 bg-amber-500 hover:bg-amber-400 text-black">
              Registra non conformità
            </Button>
          </CardContent>
        </Card>

        <h3 className="text-white text-base font-semibold mb-3">Registro</h3>
        <div className="space-y-3" data-testid="nc-list">
          {items.map((nc) => (
            <Card key={nc.id} className={`bg-[#18181b] ${nc.status === "aperta" ? "border-red-500/30" : "border-white/10"}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-medium">{nc.title}</p>
                    <p className="text-[11px] text-zinc-500">
                      {fmtDay(nc.date)} · gravità <span className={SEV[nc.severity]}>{nc.severity}</span> · {nc.signed_by?.name || "—"}
                    </p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${nc.status === "aperta" ? "bg-red-500/20 text-red-300" : "bg-emerald-500/20 text-emerald-300"}`}>
                    {nc.status}
                  </span>
                </div>
                {nc.description && <p className="text-xs text-zinc-400 mt-2">{nc.description}</p>}
                {nc.action && <p className="text-xs text-emerald-300/80 mt-1">Azione: {nc.action}</p>}
                {nc.photo_url && (
                  <a href={photoSrc(nc.photo_url)} target="_blank" rel="noreferrer">
                    <img src={photoSrc(nc.photo_url)} alt="foto NC" className="mt-2 w-full max-w-[220px] rounded-lg border border-white/10" data-testid={`nc-photo-${nc.id}`} />
                  </a>
                )}
                <div className="flex items-center gap-2 mt-3">
                  {nc.status === "aperta" && (
                    <Button size="sm" onClick={() => close(nc.id)} data-testid={`nc-close-${nc.id}`} className="h-8 bg-emerald-600 hover:bg-emerald-500 text-white text-xs">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Chiudi
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => remove(nc.id)} data-testid={`nc-delete-${nc.id}`} className="h-8 text-zinc-500 hover:text-red-400 text-xs">
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Elimina
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {!items.length && <p className="text-zinc-500 text-sm">Nessuna non conformità registrata.</p>}
        </div>
      </main>
    </div>
  );
}
